import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  BuilderCourse,
  BuilderCourseListItem,
  CreateCourseInput,
  UpdateCourseInput,
  CourseListParams,
  CourseStatus,
  CreateSectionInput,
  UpdateSectionInput,
  CreateLessonInput,
  UpdateLessonInput,
} from '@/types';
import { KEYS } from './course-builder.keys';

// ── Courses ──

export function useInstructorCourses(params?: CourseListParams) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<BuilderCourseListItem[]>('/courses', params as Record<string, string | number>),
    placeholderData: keepPreviousData,
  });
}

export function useInstructorCourseDetail(id: number) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<BuilderCourse>(`/courses/${id}`),
    select: (res) => res.data,
    enabled: id > 0,
  });
}

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCourseInput) => api.post<BuilderCourse>('/courses', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCourseInput }) =>
      api.patch<BuilderCourse>(`/courses/${id}`, data),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(v.id) });
    },
  });
}

export function useUpdateCourseStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: CourseStatus }) =>
      api.patch<BuilderCourse>(`/courses/${id}/status`, { status }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(v.id) });
    },
  });
}

export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/courses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

// ── Sections ──

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: number; data: CreateSectionInput }) =>
      api.post<unknown>(`/courses/${courseId}/sections`, data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

export function useUpdateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      data,
    }: {
      courseId: number;
      sectionId: number;
      data: UpdateSectionInput;
    }) => api.patch<unknown>(`/courses/${courseId}/sections/${sectionId}`, data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

export function useDeleteSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, sectionId }: { courseId: number; sectionId: number }) =>
      api.delete<void>(`/courses/${courseId}/sections/${sectionId}`),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

// ── Lessons ──

export function useCreateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      data,
    }: {
      courseId: number;
      sectionId: number;
      data: CreateLessonInput;
    }) => api.post<unknown>(`/courses/${courseId}/sections/${sectionId}/lessons`, data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

export function useUpdateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { lessonId: number; data: UpdateLessonInput; courseId: number }) =>
      api.patch<unknown>(`/lessons/${vars.lessonId}`, vars.data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

export function useDeleteLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { lessonId: number; courseId: number }) =>
      api.delete<void>(`/lessons/${vars.lessonId}`),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}
